import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { FileText } from "lucide-react";
import React, { useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useUpdateContractDetailsMutation } from "../../../api/useUpdateContractDetailsMutation";
import ClientConfigCard from "../components/ClientConfigCard";
import { useEditMode } from "../hooks/useEditMode";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/v2/form";
import { RadioGroup, RadioGroupCard } from "@/components/ui/v2/radio-group";
import { Switch } from "@/components/ui/v2/switch";
import { useAuth } from "@/lib/hooks/useAuth";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import useToast from "@/lib/hooks/useToast";
import {
  ClientErrorObject,
  ClientType,
  PaymentTypeEnum,
} from "@/types/client/ClientType";
import { EditClientErrorResponse } from "@/types/response/EditClientResponse";

const SECTION_NAME = "contract-details";

type ContractDetailsCardProps = {
  client?: ClientType;
  clientId: string;
  defaultPaymentType?: PaymentTypeEnum;
};

const ContractDetailsCard: React.FC<ContractDetailsCardProps> = ({
  client,
  clientId,
  defaultPaymentType,
}) => {
  const { t } = useTranslation();
  const { tenantId } = useAuth();
  const { showToast } = useToast();
  const { canEdit } = usePermissionMatrix();
  const { isEditMode, editMode, viewMode } = useEditMode(SECTION_NAME);
  const isAdmin = canEdit("clients");
  const [originalValues, setOriginalValues] = useState<{
    paymentType?: PaymentTypeEnum;
    isSelfService?: boolean;
  }>({});

  const updateMutation = useUpdateContractDetailsMutation(
    clientId,
    tenantId || ""
  );

  const schema = z.object({
    paymentType: z.enum(PaymentTypeEnum),
    isSelfService: z.boolean().optional(),
  });

  const methods = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      paymentType:
        client?.paymentType ||
        defaultPaymentType ||
        PaymentTypeEnum.SUBSCRIPTION,
      isSelfService: client?.isSelfService ?? false,
    },
  });

  const { watch, getValues, setValue, setError, handleSubmit } = methods;

  const paymentMethod =
    watch("paymentType") || client?.paymentType || defaultPaymentType;

  const paymentOptions = [
    { label: t("common.subscription"), value: PaymentTypeEnum.SUBSCRIPTION },
    { label: t("common.postpaid"), value: PaymentTypeEnum.POST_PAID },
  ];

  const handleEdit = useCallback(() => {
    const current = getValues();
    const hasChanges =
      isEditMode &&
      (current.paymentType !== originalValues.paymentType ||
        current.isSelfService !== originalValues.isSelfService);

    const canEditMode = editMode(hasChanges, () => {
      setValue(
        "paymentType",
        originalValues.paymentType ||
          client?.paymentType ||
          defaultPaymentType ||
          PaymentTypeEnum.SUBSCRIPTION
      );
      setValue(
        "isSelfService",
        originalValues.isSelfService ?? client?.isSelfService ?? false
      );
    });

    if (canEditMode) {
      setOriginalValues({
        paymentType: current.paymentType,
        isSelfService: current.isSelfService,
      });
    }
  }, [
    editMode,
    isEditMode,
    getValues,
    setValue,
    originalValues,
    client,
    defaultPaymentType,
  ]);

  const handleCancel = useCallback(() => {
    setValue(
      "paymentType",
      originalValues.paymentType ||
        client?.paymentType ||
        defaultPaymentType ||
        PaymentTypeEnum.SUBSCRIPTION
    );
    setValue(
      "isSelfService",
      originalValues.isSelfService ?? client?.isSelfService ?? false
    );
    viewMode();
  }, [originalValues, client, defaultPaymentType, setValue, viewMode]);

  const handleSave = useCallback(() => {
    const formData = getValues();
    const sectionData = {
      paymentType: formData.paymentType,
      isSelfService: formData.isSelfService,
      tenantId: tenantId || undefined,
    };

    updateMutation.mutate(sectionData, {
      onSuccess: () => {
        setOriginalValues({
          paymentType: formData.paymentType,
          isSelfService: formData.isSelfService,
        });
        viewMode();
        showToast(
          t("admin.client.clientAction.success", { action: "updated" }),
          "success"
        );
      },
      onError: (error: AxiosError<EditClientErrorResponse>) => {
        const errors = error.response?.data;
        if (errors && Array.isArray(errors)) {
          errors.forEach(
            ({ propertyName: field, errorMessage }: ClientErrorObject) => {
              if (field === "paymentType") {
                const fieldName = t(`common.${field}`);
                const message = t(`errorCodes.${errorMessage}`, { fieldName });
                setError("paymentType", {
                  type: "manual",
                  message,
                });
              } else if (field === "isSelfService") {
                const fieldName = t(`common.${field}`);
                const message = t(`errorCodes.${errorMessage}`, { fieldName });
                setError("isSelfService", {
                  type: "manual",
                  message,
                });
              }
            }
          );
        }
      },
    });
  }, [getValues, updateMutation, tenantId, t, showToast, setError, viewMode]);

  const onSubmitHandler = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleSubmit(handleSave)(e);
    },
    [handleSubmit, handleSave]
  );

  const editContent = (
    <div className="space-y-4">
      <FormField
        control={methods.control}
        name="paymentType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("admin.client.addClient.clientSettings.paymentMethod")}
            </FormLabel>
            <FormControl>
              <RadioGroup value={field.value} onValueChange={field.onChange}>
                {paymentOptions.map((option) => (
                  <RadioGroupCard
                    key={option.value}
                    id={`paymentType-${option.value}`}
                    value={option.value}
                    label={option.label}
                    disabled
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {isAdmin && (
        <FormField
          control={methods.control}
          name="isSelfService"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  {t("admin.client.selfService.label", "Self-service client")}
                </FormLabel>
                <div className="text-sm text-muted-foreground">
                  {t(
                    "admin.client.selfService.description",
                    "Enable self-service capabilities for this client"
                  )}
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
      )}
    </div>
  );

  const viewContent = (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground">
          {t("admin.client.addClient.clientSettings.paymentMethod")}
        </label>
        <div className="text-base font-normal text-foreground">
          {paymentMethod === PaymentTypeEnum.SUBSCRIPTION
            ? t("common.subscription")
            : t("common.postpaid")}
        </div>
      </div>
      {isAdmin && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">
            {t("admin.client.selfService.label", "Self-service client")}
          </label>
          <div className="text-base font-normal text-foreground">
            {client?.isSelfService
              ? t("common.selfService", "Self-service")
              : t("common.managed", "Managed")}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <FormProvider {...methods}>
      <ClientConfigCard
        title={t("admin.client.editClientTabs.contractDetails")}
        icon={FileText}
        isEditMode={isEditMode}
        isPending={updateMutation.isPending}
        onEdit={handleEdit}
        onCancel={handleCancel}
        editContent={editContent}
        onSubmit={onSubmitHandler}
      >
        {viewContent}
      </ClientConfigCard>
    </FormProvider>
  );
};

export default ContractDetailsCard;
